import _ from 'underscore';
import $ from 'jquery';
import Promise from 'bluebird';
import queryString from 'query-string';
import { useContext } from 'react';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';

import {
  isJsonEqual,
  formatFilters,
  formatLandingPageURL,
  formatTargetings,
  formatProducts,
  formatUrl,
  formatPeriod,
  formatTrafficEstimateNegatieKeywords,
  getOptions,
  getRequestSupportedLocaleId,
  redirectToLogin,
  formatIdeaOverviews,
  formatAdGroupTrafficEstimateParameters,
  formatLocationLevels,
  normalizeTrafficEstimate,
} from './util';
import { defaultIdeaAttributes, defaultIdeaAggregations, defaultDevicesTypeIds } from './config';
import {
  CONTROL_TYPES,
  IDEA_DATE_RANGE_DATA_TYPE,
  LANDSCAPE_TYPES
} from './consts';
import { searchIdeaExampleData } from '../mock/mock-data';

const urlPrefix = 'AdInsight/V2/Customers({cid})/Accounts({aid})';
const urlPrefixLocation = 'Advertiser/V2/Customers({cid})/Accounts({aid})';
const getAvailableDateRangeAPI = `${urlPrefix}/Default.IdeaDataRanges`;
const getIdeaCategoryDataAPI = `${urlPrefix}/Default.IdeaCategories?$expand=SubCategories($levels=max)`;
const getSearchIdeaAPI = `${urlPrefix}/Default.SuggestionPerformanceIdeas`;
const getTrafficEstimateAPI = `${urlPrefix}/Default.CampaignTrafficEstimateMultifunction?$expand=AdGroupEstimatedTraffics($expand=KeywordEstimatedTraffics)`;
const getLocationsAPI = `${urlPrefixLocation}/Locations`;

export const useKeywordPlannerService = () => {
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const { scope: { values: scopeValues } } = useContext(ScopeContext);
  const permissions = useContext(PermissionsContext);
  const {
    CurrentUser: { LocaleID: lcid },
    CurrentLocale: locale,
    ServicesEndpoints: { AdInsightAPI: odataPath, CampaignAPI: odataCampaignPath },
    Token: token,
  } = serverContext;
  let lastSearchIdeaRequestBody = null; // memorize last input to compare with the new one
  let lastTrafficEstimateRequestBody = null; // memorize last input to compare with the new on

  const handleAjaxError = (err) => {
    // TODO: log
    if (err.status === 401) {
      redirectToLogin(err);
    }
    notificationApi.showWarning(i18n.getString(_TL_('We\'ve encountered an error and can\'t continue. Please contact support for further assistance.')));
  };

  const generateGetAvailableDateRangeRequest = () => {
    const data = {
      datatype: IDEA_DATE_RANGE_DATA_TYPE.SEARCH_VOLUME,
    };
    const option = getOptions({
      token,
      lcid,
      data,
    });

    option.url = formatUrl({ url: `${odataPath}${getAvailableDateRangeAPI}`, scopeValues });
    return option;
  };

  const getAvailableDateRange = () => {
    const option = generateGetAvailableDateRangeRequest();
    if (!option) {
      return Promise.resolve({ data: [] });
    }
    const fetchDataPromise = new Promise((resolve, reject) => {
      $.ajax(option).then((rawData) => {
        resolve(rawData);
      }, reject);
    });

    return fetchDataPromise.then(
      rawData => ({ data: rawData }),
      (err) => {
        handleAjaxError(err);
        return {};
      }
    );
  };

  const generateGetIdeaCategoryDataRequest = () => {
    const option = getOptions({
      token,
      lcid,
    });

    option.url = formatUrl({ url: `${odataPath}${getIdeaCategoryDataAPI}`, scopeValues });
    return option;
  };

  const getIdeaCategoryData = () => {
    const option = generateGetIdeaCategoryDataRequest();
    if (!option) {
      return Promise.resolve({ data: [] });
    }
    const fetchDataPromise = new Promise((resolve, reject) => {
      $.ajax(option).then((rawData) => {
        resolve(rawData);
      }, reject);
    });

    return fetchDataPromise.then(
      rawData => ({ data: rawData }),
      (err) => {
        handleAjaxError(err);
        return {};
      }
    );
  };

  const generateGetSearchIdeaRequestRequest = ({
    model, mergeLocation = false, useCategoryFilter = false, isLanguageDecoupling = false, channel,
  }) => {
    // undefined property will be removed by stringify
    const products = formatProducts(model);
    const isCategoryFilter =
      !_.isEmpty(products) || !_.isEmpty(model[CONTROL_TYPES.LANDING_PAGE].url);
    // When language decoupling is enabled in search volumne, we don't need to send language in the request body.
    const isLanguageSkipped = isLanguageDecoupling && !model.isIdeas;
    const data = {
      SearchIdeaParameter: {
        IsIdeas: model.isIdeas,
        Products: products,
        LandingPage: model[CONTROL_TYPES.LANDING_PAGE].url ?
          formatLandingPageURL(model[CONTROL_TYPES.LANDING_PAGE]) : undefined,
        CategoryId: model[CONTROL_TYPES.CATEGORY].categoryId && !isCategoryFilter ?
          Number(model[CONTROL_TYPES.CATEGORY].categoryId) : undefined,
        Targetings: formatTargetings(model, isLanguageDecoupling, isLanguageSkipped),
        Filters: formatFilters({
          model,
          scopeValues,
          isCategoryFilter: useCategoryFilter && isCategoryFilter,
        }),
        PeriodSetting: formatPeriod(model),
        IdeaOverviews: formatIdeaOverviews(model, mergeLocation),
        IdeaAttributes: mergeLocation ? undefined : defaultIdeaAttributes,
        IdeaAggregations: mergeLocation ? undefined : defaultIdeaAggregations,
        Locale: getRequestSupportedLocaleId({ localeString: locale }),
      },
    };
    const option = getOptions({
      token,
      lcid,
      data,
      channel,
    });

    option.url = formatUrl({ url: `${odataPath}${getSearchIdeaAPI}`, scopeValues });
    return option;
  };

  const getSearchIdea = ({
    model, mergeLocation, useCategoryFilter = true, isLanguageDecoupling = false, channel,
  }) => {
    const option = generateGetSearchIdeaRequestRequest({
      model,
      mergeLocation,
      useCategoryFilter,
      isLanguageDecoupling,
      channel,
    });

    // if request body is the same, don't send duplicated MT request
    if (isJsonEqual(lastSearchIdeaRequestBody, option.data)) {
      return Promise.resolve({ isDirty: false });
    }
    lastSearchIdeaRequestBody = option.data;

    let fetchDataPromise = null;
    if (window.e2eTestEnvironment) {
      fetchDataPromise = Promise.resolve({
        rawData: { value: searchIdeaExampleData },
        jqXHR: { getResponseHeader: () => '' },
      });
    } else {
      fetchDataPromise = new Promise((resolve, reject) => {
        $.ajax(option).then((rawData, _textStatus, jqXHR) => {
          resolve({ rawData, jqXHR });
        }, reject);
      });
    }

    return fetchDataPromise.then(
      ({ rawData, jqXHR }) => ({
        data: rawData,
        isDirty: true,
        traceInfo: {
          requestId: jqXHR ? jqXHR.getResponseHeader('x-ms-requestid') : '',
          trackId: jqXHR ? jqXHR.getResponseHeader('x-ms-trackingid') : '',
          locationId: _.pluck(model[CONTROL_TYPES.LOCATION], 'id'),
        },
      }),
      (err) => {
        handleAjaxError(err);
        throw (err);
      }
    );
  };

  const generateGetSearchLocationsRequest = (search) => {
    const data = {
      $search: search,
      $top: 20,
      $count: true,
      $expand: 'ParentLocations',
    };
    const option = getOptions({
      token,
      lcid,
    });

    const url = formatUrl({ url: `${odataCampaignPath}${getLocationsAPI}`, scopeValues });
    option.url = `${url}?${queryString.stringify(data)}`;
    return option;
  };

  const getSearchLocations = (text) => {
    const option = generateGetSearchLocationsRequest(text);
    if (!option) {
      return Promise.resolve({ data: [] });
    }

    const fetchDataPromise = new Promise((resolve, reject) => {
      $.ajax(option).then((rawData) => {
        resolve(rawData);
      }, reject);
    });

    return fetchDataPromise.then(
      rawData => ({ data: rawData }),
      (err) => {
        handleAjaxError(err);
        return {};
      }
    );
  };

  const generateGetTrafficEstimateRequest = (model) => {
    const {
      AdGroupTrafficEstimateParameters,
      scaleLandScape,
      requestLandscape,
    } = formatAdGroupTrafficEstimateParameters(model);
    const isLanguageDecoupling = !permissions.IsDomesticSimplifiedChineseEnabled;
    const isLanguageSkipped = isLanguageDecoupling;
    const data = {
      CampaignTrafficEstimate: {
        Locale: getRequestSupportedLocaleId({ localeString: locale }),
        Budget: model[CONTROL_TYPES.TRAFFIC_ESTIMATE_BUDGET] ?
          model[CONTROL_TYPES.TRAFFIC_ESTIMATE_BUDGET] : undefined,
        NegativeKeywords: formatTrafficEstimateNegatieKeywords(model),
        Targetings: formatTargetings(model, isLanguageDecoupling, isLanguageSkipped),
        DevicesTypeIds: model[CONTROL_TYPES.TRAFFIC_ESTIMATE_BID] ?
          defaultDevicesTypeIds : undefined,
        LocationLevels: model[CONTROL_TYPES.TRAFFIC_ESTIMATE_BID] ?
          formatLocationLevels(model) : undefined,
        LandscapeEstimateParameters: requestLandscape ?
          [{ LandscapeType: (scaleLandScape && LANDSCAPE_TYPES.SCALE) || LANDSCAPE_TYPES.BID }] :
          undefined,
        AdGroupTrafficEstimateParameters,
      },
    };
    const option = getOptions({
      token,
      lcid,
      data,
    });

    option.url = formatUrl({ url: `${odataPath}${getTrafficEstimateAPI}`, scopeValues });
    return option;
  };

  const getTrafficEstimate = ({ model, mergeLocation }) => {
    const option = generateGetTrafficEstimateRequest(model, mergeLocation);
    if (!option) {
      return Promise.resolve({ data: [] });
    }

    // if keyword is empty, don't send request
    if (_.isEmpty(model[CONTROL_TYPES.TRAFFIC_ESTIMATE_ADGROUP_KEYWORDS])) {
      lastTrafficEstimateRequestBody = null;
      return Promise.resolve({
        data: normalizeTrafficEstimate({
          model,
          rawData: { value: [{}] },
          i18n,
        }),
        isDirty: true,
      });
    }
    // if request body is the same, don't send duplicated MT request
    if (isJsonEqual(lastTrafficEstimateRequestBody, option.data)) {
      return Promise.resolve({ isDirty: false });
    }
    lastTrafficEstimateRequestBody = option.data;

    const fetchDataPromise = new Promise((resolve, reject) => {
      $.ajax(option).then((rawData, _textStatus, jqXHR) => {
        resolve({ rawData, jqXHR });
      }, reject);
    });

    return fetchDataPromise.then(
      ({ rawData, jqXHR }) => ({
        data: normalizeTrafficEstimate({
          model,
          rawData,
          i18n,
        }),
        isDirty: true,
        traceInfo: {
          requestId: jqXHR.getResponseHeader('x-ms-requestid'),
          trackId: jqXHR.getResponseHeader('x-ms-trackingid'),
          locationId: _.pluck(model[CONTROL_TYPES.LOCATION], 'id'),
        },
      }),
      (err) => {
        handleAjaxError(err);
        return {};
      }
    );
  };

  return {
    getAvailableDateRange,
    getIdeaCategoryData,
    getSearchIdea,
    getSearchLocations,
    getTrafficEstimate,
  };
};
